import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Send as SendIcon,
  MoreVert as MoreVertIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTaskComments, createComment, deleteComment, updateComment } from '../store/slices/commentSlice';
import { AppDispatch, RootState } from '../store';
import { formatDistanceToNow } from 'date-fns';

interface CommentItemProps {
  comment: any;
  onReply: (commentId: string) => void;
  onEdit: (comment: any) => void;
  onDelete: (commentId: string) => void;
  level?: number;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, onReply, onEdit, onDelete, level = 0 }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <Box sx={{ ml: level * 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2 }}>
        <Avatar sx={{ width: 40, height: 40 }}>{comment.authorName[0]}</Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {comment.authorName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {comment.content}
          </Typography>
          <Button
            size="small"
            startIcon={<ReplyIcon />}
            onClick={() => onReply(comment.id)}
          >
            Reply
          </Button>
        </Box>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => { onEdit(comment); setAnchorEl(null); }}>
            Edit
          </MenuItem>
          <MenuItem onClick={() => { onDelete(comment.id); setAnchorEl(null); }}>
            Delete
          </MenuItem>
        </Menu>
      </Box>
      {comment.replies && comment.replies.length > 0 && (
        <Box>
          {comment.replies.map((reply: any) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              level={level + 1}
            />
          ))}
        </Box>
      )}
      <Divider />
    </Box>
  );
};

const CommentsSection: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { comments, loading } = useSelector((state: RootState) => state.comments);
  
  const [newComment, setNewComment] = useState('');
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [editingComment, setEditingComment] = useState<any>(null);

  const taskComments = taskId ? comments[taskId] || [] : [];

  useEffect(() => {
    if (taskId) {
      dispatch(fetchTaskComments(taskId));
    }
  }, [dispatch, taskId]);

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !taskId) return;

    if (editingComment) {
      await dispatch(updateComment({ id: editingComment.id, data: { content: newComment } }));
      setEditingComment(null);
    } else {
      await dispatch(createComment({
        taskId,
        content: newComment,
        parentCommentId: replyToId,
      }));
    }

    setNewComment('');
    setReplyToId(null);
  };

  const handleDeleteComment = async (commentId: string) => {
    if (taskId && window.confirm('Delete this comment?')) {
      await dispatch(deleteComment({ id: commentId, taskId }));
    }
  };

  const handleReply = (commentId: string) => {
    setReplyToId(commentId);
  };

  const handleEdit = (comment: any) => {
    setEditingComment(comment);
    setNewComment(comment.content);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Comments
      </Typography>

      <Paper sx={{ mb: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : taskComments.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No comments yet. Be the first to comment!
            </Typography>
          </Box>
        ) : (
          <Box>
            {taskComments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onReply={handleReply}
                onEdit={handleEdit}
                onDelete={handleDeleteComment}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* New Comment Input */}
      <Paper sx={{ p: 2 }}>
        {replyToId && (
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="primary">
              Replying to comment
            </Typography>
            <Button size="small" onClick={() => setReplyToId(null)}>
              Cancel
            </Button>
          </Box>
        )}
        {editingComment && (
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="primary">
              Editing comment
            </Typography>
            <Button size="small" onClick={() => { setEditingComment(null); setNewComment(''); }}>
              Cancel
            </Button>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                handleSubmitComment();
              }
            }}
          />
          <Button
            variant="contained"
            endIcon={<SendIcon />}
            onClick={handleSubmitComment}
            disabled={!newComment.trim()}
          >
            {editingComment ? 'Update' : 'Send'}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Press Ctrl+Enter to send
        </Typography>
      </Paper>
    </Container>
  );
};

export default CommentsSection;
